import { fetchAndDecode } from './audioFetcher'
import { getAudioContext, resumeAudioContext } from './audioContext'

export type PlaybackState = {
  isPlaying: boolean
  currentTime: number
  duration: number
  currentAyahIndex: number
  loading: boolean
  error: string | null
  volume: number
}

export type QueueItem = {
  url: string
  index: number              // index in the *current* surah
  surahNumber?: number       // present when crossing a surah boundary (Task 9)
  ayahNumber?: number
  surahData?: unknown        // Awaited<ReturnType<typeof quranApi.getSurahWithTranslation>>
}

export type QueueProvider = (currentIndex: number) => Promise<QueueItem | null>

// A next-ayah fetch that scheduleNext has kicked off but not yet settled.
// handleEnded reads this to tell "queue exhausted" (queueProvider returned
// null — nothing here) apart from "still downloading" (this is set) when the
// current source ends before the pre-fetch finishes. See the slow-network
// boundary handling in scheduleNext/handleEnded/continueBuffering below.
type PendingNext = { token: number; item: QueueItem; promise: Promise<AudioBuffer> }

export type EngineOptions = {
  onStateChange: (state: PlaybackState) => void
  onAyahChange: (index: number, item: QueueItem) => void
  onSurahHandoff?: (item: QueueItem) => void
  queueProvider: QueueProvider
}

const initialState: PlaybackState = {
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  currentAyahIndex: 0,
  loading: false,
  error: null,
  volume: 1,
}

export class PlaybackEngine {
  private state: PlaybackState = { ...initialState }
  // currentSource is cleared on dispose; the rest survive (used by pause/resume in Task 8).
  private currentSource: AudioBufferSourceNode | null = null
  private currentBuffer: AudioBuffer | null = null
  private currentItem: QueueItem | null = null
  private startedAt = 0
  private pauseOffset = 0
  private rafId: number | null = null

  private scheduledNextSource: AudioBufferSourceNode | null = null
  private scheduledNextBuffer: AudioBuffer | null = null
  private scheduledNextItem: QueueItem | null = null
  private scheduleToken = 0  // bumped on every (re)schedule; stale callbacks bail
  private scheduledNextWhen = 0  // audio-clock time at which scheduledNextSource will begin

  // Set in scheduleNext for the duration of the next-ayah fetch, cleared once
  // it settles (success or failure) or once its token goes stale. Lets
  // handleEnded distinguish "still downloading" from "queue exhausted" at the
  // exact moment the current source ends.
  private pendingNext: PendingNext | null = null

  // Small slack so a `when` that's landed exactly on (or a hair past)
  // ctx.currentTime is still treated as "in the past" — see scheduleNext.
  private static readonly PAST_WHEN_EPSILON_SEC = 0.01

  // Throttle for the RAF currentTime fan-out (see startRafLoop). Updating at
  // full 60Hz forced every consumer of audioPlayer (VerseList, AudioPlayer,
  // PlaylistDetailPage hero, mini-player, etc.) to re-render 60×/s — heavy
  // enough to choke the main thread and make click handlers like the volume
  // button feel dead. 4Hz still looks smooth for the progress bar and is
  // 15× less React work than full 60Hz. The boundary check (ayah handoff)
  // stays unthrottled so the highlight still flips on the exact frame.
  private lastTimeFlushMs = 0
  private static readonly TIME_FLUSH_MIN_MS = 250

  // Single GainNode sits between every source and the destination so volume
  // changes apply uniformly to current + scheduled-next sources, and survive
  // seek/rate changes (which rebuild the source but reuse this node).
  private gainNode: GainNode | null = null

  private disposed = false

  constructor(private readonly opts: EngineOptions) {}

  /** Lazily create the shared GainNode the first time we need it. */
  private getGainNode(): GainNode {
    if (this.gainNode) return this.gainNode
    const ctx = getAudioContext()
    const gain = ctx.createGain()
    gain.gain.value = this.state.volume
    gain.connect(ctx.destination)
    this.gainNode = gain
    return gain
  }

  getState(): PlaybackState {
    return this.state
  }

  dispose(): void {
    this.disposed = true
    this.scheduleToken++
    this.disposeScheduledNext()
    this.disposeCurrentSource()
    this.stopRafLoop()
    this.setState({ isPlaying: false })
  }

  async play(item: QueueItem): Promise<void> {
    if (this.disposed) return
    this.setState({ loading: true, error: null })
    try {
      await resumeAudioContext()
      const buffer = await fetchAndDecode(item.url)
      this.startBuffer(buffer, item, 0)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      this.setState({
        loading: false,
        isPlaying: false,
        error: `Failed to load audio: ${msg}`,
      })
    }
  }

  pause(): void {
    if (!this.currentBuffer) return
    const ctx = getAudioContext()
    const elapsed = ctx.currentTime - this.startedAt
    this.pauseOffset = Math.max(0, Math.min(elapsed, this.currentBuffer.duration))
    this.scheduleToken++  // invalidate any pending scheduling (incl. a buffering continuation)
    this.disposeScheduledNext()
    this.disposeCurrentSource()
    this.stopRafLoop()
    // loading:false matters when pause() lands mid-buffering (handleEnded's
    // continuation set loading:true while awaiting a still-in-flight next-
    // ayah fetch). Without this, AudioPlayer — which disables play/pause/
    // seek/next/prev while loading and shows a spinner — would strand the
    // user on an eternal spinner: the token bump above cancels the
    // continuation, but continueBuffering's stale-token bail is silent and
    // never itself clears loading. Harmless pre-playback: the currentBuffer
    // guard above already makes pause() a no-op before anything has loaded.
    this.setState({ isPlaying: false, loading: false, currentTime: this.pauseOffset })
  }

  async resume(): Promise<void> {
    if (this.disposed) return
    if (this.state.isPlaying) return
    if (!this.currentBuffer || !this.currentItem) return
    await resumeAudioContext()
    this.startBuffer(this.currentBuffer, this.currentItem, this.pauseOffset)
  }

  seek(time: number): void {
    if (this.disposed) return
    if (!this.currentBuffer || !this.currentItem) return
    const offset = Math.max(0, Math.min(time, this.currentBuffer.duration))
    this.startBuffer(this.currentBuffer, this.currentItem, offset)
  }

  setVolume(value: number): void {
    if (this.disposed) return
    const clamped = Math.max(0, Math.min(value, 1))
    this.setState({ volume: clamped })
    // Push to the live GainNode if it's been created. We only build the node
    // lazily on first playback, so a pre-playback setVolume just updates state
    // and the node will pick it up when getGainNode() is first invoked.
    if (this.gainNode) {
      this.gainNode.gain.value = clamped
    }
  }

  get volume(): number {
    return this.state.volume
  }

  async next(): Promise<void> {
    if (this.disposed) return
    if (!this.currentItem) return
    const next = await this.opts.queueProvider(this.currentItem.index)
    if (next) await this.play(next)
  }

  async prev(): Promise<void> {
    if (this.disposed) return
    if (!this.currentItem) return
    // Convention: queueProvider(i) returns "what comes after i".
    // To get the item BEFORE the current index, ask for the item after (index - 2).
    const prev = await this.opts.queueProvider(this.currentItem.index - 2)
    if (prev) await this.play(prev)
  }

  // ---- internals ----

  private setState(patch: Partial<PlaybackState>): void {
    this.state = { ...this.state, ...patch }
    this.opts.onStateChange(this.state)
  }

  private startBuffer(buffer: AudioBuffer, item: QueueItem, offset: number): void {
    // Clear the scheduled-next FIRST. Without this, prev/seek/rate-change leave the
    // previously-armed source on the audio clock and it fires later, overlapping
    // the new playback. Bumping scheduleToken alone doesn't stop the audio thread
    // from honoring its prior start(when) appointment.
    this.scheduleToken++
    this.disposeScheduledNext()
    this.disposeCurrentSource()

    const ctx = getAudioContext()
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(this.getGainNode())
    source.start(0, offset)

    this.currentSource = source
    this.currentBuffer = buffer
    this.currentItem = item
    this.startedAt = ctx.currentTime - offset
    this.pauseOffset = offset

    const myToken = ++this.scheduleToken
    source.onended = () => this.handleEnded(myToken)

    this.setState({
      isPlaying: true,
      loading: false,
      currentAyahIndex: item.index,
      duration: buffer.duration,
      currentTime: offset,
      error: null,
    })
    this.opts.onAyahChange(item.index, item)
    this.startRafLoop()
    void this.scheduleNext(myToken)
  }

  private disposeCurrentSource(): void {
    if (!this.currentSource) return
    try {
      this.currentSource.onended = null
      this.currentSource.stop()
      this.currentSource.disconnect()
    } catch { /* ignore — already stopped */ }
    this.currentSource = null
  }

  private startRafLoop(): void {
    this.stopRafLoop()
    if (typeof requestAnimationFrame === 'undefined') return
    // Reset the throttle baseline so the very first tick fires immediately
    // (otherwise the progress bar would hold at 0 for up to ~100ms on play).
    this.lastTimeFlushMs = 0
    const tick = () => {
      if (!this.state.isPlaying || !this.currentBuffer) return
      const ctx = getAudioContext()
      // startedAt was set so that (currentTime - startedAt) * rate == position in buffer.
      const position = ctx.currentTime - this.startedAt
      const clamped = Math.max(0, Math.min(position, this.currentBuffer.duration))

      // Throttled currentTime fan-out. Hits the engine state (and React via
      // onStateChange) at most ~10×/s. The boundary check below stays at
      // full RAF cadence so the highlight still flips on the exact frame
      // the audio advances.
      const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
      if (nowMs - this.lastTimeFlushMs >= PlaybackEngine.TIME_FLUSH_MIN_MS) {
        this.lastTimeFlushMs = nowMs
        this.setState({ currentTime: clamped })
      }

      // Proactively flip to the scheduled-next ayah as soon as the audio
      // clock crosses its start time. Without this we'd wait for the OLD
      // source's `onended` callback to fire on the main thread, which lags
      // the actual audio boundary by 1-3 frames (worse on Safari) — making
      // the highlight visually drift behind the recitation. flipToScheduledNext
      // is idempotent via the scheduleToken: when handleEnded later runs for
      // the disposed source, its token will be stale and it'll bail.
      if (
        this.scheduledNextItem &&
        this.scheduledNextWhen > 0 &&
        ctx.currentTime >= this.scheduledNextWhen
      ) {
        this.flipToScheduledNext()
      }

      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }

  private stopRafLoop(): void {
    if (this.rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.rafId)
    }
    this.rafId = null
  }

  private async scheduleNext(token: number): Promise<void> {
    if (token !== this.scheduleToken) return
    if (!this.currentItem || !this.currentBuffer) return
    const next = await this.opts.queueProvider(this.currentItem.index)
    if (token !== this.scheduleToken) return
    if (!next) {
      this.scheduledNextSource = null
      this.scheduledNextBuffer = null
      this.scheduledNextItem = null
      return
    }

    // Track the in-flight fetch so handleEnded can tell "queue exhausted"
    // apart from "still downloading" if the current buffer ends before this
    // resolves. fetchWithRetry also absorbs a transient blip here — this used
    // to be a bare fetchAndDecode whose catch silently gave up, guaranteeing
    // a dead stop at the boundary for any dropped request.
    const fetchPromise = this.fetchWithRetry(next.url)
    const pending: PendingNext = { token, item: next, promise: fetchPromise }
    this.pendingNext = pending

    let nextBuffer: AudioBuffer
    try {
      nextBuffer = await fetchPromise
    } catch {
      // Silent — matches the pre-existing contract. If the boundary hasn't
      // been hit yet, natural `ended` will find pendingNext already cleared
      // (below) and stop normally, same as "no next". If the boundary WAS
      // hit while this was in flight, handleEnded's buffering continuation
      // is awaiting this exact promise too and reports the failure itself.
      if (this.pendingNext === pending) this.pendingNext = null
      return
    }
    if (token !== this.scheduleToken) return
    if (!this.currentBuffer) return

    const ctx = getAudioContext()
    const when = this.startedAt + this.currentBuffer.duration
    // Past-when guard: if the boundary has already gone by (the slow-network
    // case — handleEnded already fired, or is about to, and owns buffering on
    // this very promise), don't arm a source here. Web Audio clamps a past
    // `when` to "now", so this would ghost-start the next ayah with none of
    // the engine's bookkeeping wired to it (no onended, no RAF, no
    // onAyahChange) while state still says "buffering"/"stopped".
    //
    // Deliberately do NOT clear pendingNext before this check (and don't
    // clear it here if we bail): handleEnded may fire moments after this
    // returns — real onended dispatch lags the audio clock crossing the
    // boundary by roughly a frame or more — and it needs to find pendingNext
    // still set (with a still-valid token) so it takes the buffering
    // continuation instead of "genuine stop". Since this promise has already
    // resolved by the time that happens, continueBuffering's `await` settles
    // on the very next microtask — the handoff is effectively immediate, not
    // an extra buffering delay. Clearing pendingNext unconditionally right
    // after the fetch settled (the previous version of this code) dropped
    // exactly this handoff: the guard would refuse to arm here, but
    // pendingNext was already gone, so the imminent handleEnded found
    // nothing pending and nothing armed and stopped dead with the decoded
    // buffer in hand and nowhere to put it.
    if (when <= ctx.currentTime + PlaybackEngine.PAST_WHEN_EPSILON_SEC) return

    // We're actually arming — this cycle's pendingNext is spent either way
    // (token staleness and the genuine-stop branch's clear cover the rest).
    if (this.pendingNext === pending) this.pendingNext = null

    const nextSource = ctx.createBufferSource()
    nextSource.buffer = nextBuffer
    nextSource.connect(this.getGainNode())
    nextSource.start(when)

    this.scheduledNextSource = nextSource
    this.scheduledNextBuffer = nextBuffer
    this.scheduledNextItem = next
    this.scheduledNextWhen = when
  }

  private handleEnded(token: number): void {
    if (token !== this.scheduleToken) return

    if (this.pendingNext && this.pendingNext.token === this.scheduleToken) {
      // The current buffer ended but the next ayah is still downloading
      // (slow network). The old behavior fell through to "no next — stop"
      // here: it couldn't tell "queue exhausted" apart from "still
      // fetching", so it stopped dead without bumping scheduleToken or
      // clearing currentBuffer/currentItem. When the slow fetch resolved
      // later, scheduleNext's guards still passed against the untouched
      // token/currentBuffer, and it called start() at a `when` already in
      // the past — Web Audio clamps that to "now" and the next ayah
      // audibly ghost-starts while the UI still shows the frozen/stopped
      // state (no onended, no RAF, no onAyahChange wired to it).
      //
      // Instead: keep "playing" intent (isPlaying stays true — the user
      // hasn't paused, we're just waiting on the network), surface loading
      // so the UI can show buffering instead of a frozen highlight, and
      // park the RAF loop (nothing is on the audio clock right now, so
      // there's nothing to animate). Then await the SAME promise
      // scheduleNext is already awaiting — continueBuffering below.
      const pending = this.pendingNext
      this.disposeCurrentSourceWithoutStop() // it ended naturally
      this.stopRafLoop()
      this.setState({ loading: true })
      void this.continueBuffering(pending)
      return
    }

    if (!this.scheduledNextSource || !this.scheduledNextBuffer || !this.scheduledNextItem) {
      // Genuine end of queue: queueProvider returned null and nothing is in
      // flight. Bump scheduleToken and drop pendingNext defensively so a
      // late-settling fetch from a superseded schedule cycle can never
      // ghost-start a source after we've already told the UI playback
      // stopped.
      this.scheduleToken++
      this.pendingNext = null
      this.disposeCurrentSource()
      this.stopRafLoop()
      this.setState({ isPlaying: false, currentTime: this.currentBuffer?.duration ?? 0 })
      return
    }
    // Hand off to the same flip logic the RAF loop uses. Whichever fires
    // first wins; the loser bails on the stale token check above.
    this.flipToScheduledNext()
  }

  /**
   * Awaits the fetch handleEnded found already in flight at the boundary
   * (the buffering branch above) and either continues playback or reports
   * the failure. Bails silently if a pause()/startBuffer() (seek, rate
   * change, next/prev, dispose, or a fresh play()) has bumped scheduleToken
   * since we started waiting — the user has already moved on, so there is
   * nothing left to hand off into and nothing to report.
   */
  private async continueBuffering(pending: PendingNext): Promise<void> {
    let buffer: AudioBuffer
    try {
      buffer = await pending.promise
    } catch (err) {
      if (pending.token !== this.scheduleToken) return
      const msg = err instanceof Error ? err.message : String(err)
      this.scheduleToken++
      this.setState({ isPlaying: false, loading: false, error: `Failed to load audio: ${msg}` })
      return
    }
    if (pending.token !== this.scheduleToken) return
    // startBuffer re-wires onended/RAF/onAyahChange and schedules the ayah
    // after this one — exactly the state a normal handoff would have set up.
    this.startBuffer(buffer, pending.item, 0)
  }

  /**
   * Bounded retries for the scheduled-next (pre-arm) path only. A dropped
   * request or transient 5xx here used to guarantee a dead stop at the ayah
   * boundary — scheduleNext's catch was silent with no retry. play()'s
   * direct fetch is untouched; it already surfaces its own error UI.
   */
  private fetchWithRetry(url: string, retries = 2, backoffMs = 300): Promise<AudioBuffer> {
    const attempt = (n: number): Promise<AudioBuffer> =>
      fetchAndDecode(url).catch((err: unknown) => {
        if (n >= retries) throw err
        return new Promise<AudioBuffer>((resolve, reject) => {
          setTimeout(() => {
            attempt(n + 1).then(resolve, reject)
          }, backoffMs * (n + 1))
        })
      })
    return attempt(0)
  }

  /**
   * Promote the pre-scheduled next source to "current" and fire the state
   * update + onAyahChange callback. Bumps scheduleToken so any in-flight
   * onended callback from the previous source becomes a no-op when it
   * eventually fires.
   *
   * Called from two places:
   *   1. RAF loop, when ctx.currentTime crosses scheduledNextWhen. This is
   *      the "highlight stays in sync" path — it fires within ~16ms of the
   *      actual audio boundary regardless of how late onended dispatches.
   *   2. handleEnded, as the fallback path when scheduledNextWhen wasn't set
   *      yet (e.g. the next item finished decoding very close to end-of-buffer).
   *
   * Idempotent: returns early if there's nothing scheduled.
   */
  private flipToScheduledNext(): void {
    if (!this.scheduledNextSource || !this.scheduledNextBuffer || !this.scheduledNextItem) {
      return
    }
    // The scheduled source is already playing on the audio thread.
    this.disposeCurrentSourceWithoutStop() // it ended naturally; don't call stop()
    this.currentSource = this.scheduledNextSource
    this.currentBuffer = this.scheduledNextBuffer
    this.currentItem = this.scheduledNextItem
    this.startedAt = this.scheduledNextWhen
    this.pauseOffset = 0

    const newToken = ++this.scheduleToken
    this.currentSource.onended = () => this.handleEnded(newToken)

    const handedOffItem = this.currentItem
    this.scheduledNextSource = null
    this.scheduledNextBuffer = null
    this.scheduledNextItem = null
    this.scheduledNextWhen = 0

    this.setState({
      currentAyahIndex: this.currentItem.index,
      duration: this.currentBuffer.duration,
      currentTime: 0,
    })
    this.opts.onAyahChange(this.currentItem.index, this.currentItem)
    if (handedOffItem.surahData && this.opts.onSurahHandoff) {
      this.opts.onSurahHandoff(handedOffItem)
    }
    void this.scheduleNext(newToken)
  }

  private disposeCurrentSourceWithoutStop(): void {
    if (!this.currentSource) return
    try {
      this.currentSource.onended = null
      this.currentSource.disconnect()
    } catch { /* ignore */ }
    this.currentSource = null
  }

  private disposeScheduledNext(): void {
    if (!this.scheduledNextSource) return
    try {
      this.scheduledNextSource.onended = null
      this.scheduledNextSource.stop()
      this.scheduledNextSource.disconnect()
    } catch { /* ignore */ }
    this.scheduledNextSource = null
    this.scheduledNextBuffer = null
    this.scheduledNextItem = null
  }
}
